import { makeAutoObservable, runInAction } from 'mobx';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { DateTime } from 'luxon';
import { envIsDev } from '../../utils/environment';
import { newKeycloakInstance } from './keycloakConfig';
import { setupMobX } from '../../utils';
import { requestDeleteUser } from '../userDataStore';
import { badRequestException, ErrorCode } from '@web/common';

const isDev = envIsDev();

export class KeycloakStore {
  public initializingInProgress = false;
  public initialized = false;
  public failedInitialization = false;
  public authenticated = false;
  public realmRoleList: string[] | null = null;
  public user: KeycloakProfile | null = null;
  public disableKc = false;
  private keycloak: null | Keycloak = null;
  private tokenRefresherHandle: NodeJS.Timeout | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  public disableKeycloak() {
    // ensures that keycloak can only be disabled in dev mode
    if (!isDev) return;

    runInAction(() => {
      this.disableKc = true;
      this.initialized = true;
      this.failedInitialization = false;
      this.initializingInProgress = false;
    });
    console.debug('Keycloak disabled');
  }

  public async initKeycloak() {
    if (this.initializingInProgress || this.initialized) return;

    runInAction(() => {
      this.initializingInProgress = true;
      this.failedInitialization = false;
    });

    const keycloak = newKeycloakInstance();

    try {
      const authenticated = await keycloak.init({ onLoad: 'check-sso' });

      if (authenticated) {
        console.debug('✅ Keycloak initialized successfully (authenticated)');
        const userInfo = await keycloak.loadUserProfile();
        runInAction(() => {
          this.keycloak = keycloak;
          this.authenticated = true;
          this.initialized = true;
          this.user = userInfo;
          //this.realmRoleList = this.extractRoles(keycloak);
        });
        this.setupAccessTokenRefresh(keycloak);
        await this.updateToken();
      } else {
        console.debug('✅ Keycloak initialized successfully');
        runInAction(() => {
          this.keycloak = keycloak;
          this.authenticated = false;
          this.initialized = true;
        });
      }
    } catch (error) {
      console.error('❌ Keycloak init failed:', error);
      runInAction(() => (this.failedInitialization = true));
    } finally {
      runInAction(() => (this.initializingInProgress = false));
    }
  }

  public async login(redirectUri?: string) {
    runInAction(() => {
      this.authenticated = false;
      this.user = null;
      this.realmRoleList = null;
    });

    void this.keycloak?.login({ redirectUri }); // Redirect to login
  }

  public async logout(redirectUri?: string) {
    if (!this.keycloak) return;

    try {
      await this.keycloak.logout({
        redirectUri: redirectUri || window.location.origin,
      });
    } catch (error) {
      console.error('❌ Failed to logout:', error);
    } finally {
      runInAction(() => {
        this.authenticated = false;
        this.user = null;
        this.realmRoleList = null;
        this.initialized = false;
        this.failedInitialization = false;
      });

      if (this.tokenRefresherHandle) {
        clearTimeout(this.tokenRefresherHandle);
        this.tokenRefresherHandle = null;
      }

      this.keycloak = null;
    }
  }

  public async deleteUser() {
    if (!this.keycloak) return;
    if (!this.user?.id) return;

    try {
      await requestDeleteUser(this.token);
    } catch (error) {
      console.error('❌ Failed to delete user:', error);
      throw badRequestException(
        ErrorCode.KeycloakUserDeletionFailed,
        '❌ Failed to delete user',
      );
    } finally {
      runInAction(() => {
        this.authenticated = false;
        this.user = null;
        this.realmRoleList = null;
        this.initialized = false;
        this.failedInitialization = false;
      });

      if (this.tokenRefresherHandle) {
        clearTimeout(this.tokenRefresherHandle);
        this.tokenRefresherHandle = null;
      }

      this.keycloak = null;
    }
  }

  public async updateToken() {
    if (!this.keycloak) return;

    try {
      const minValidity = 60; // refresh if expiring in < 60s
      const refreshed = await this.keycloak.updateToken(minValidity);

      if (refreshed) {
        console.debug('🔄 Token was refreshed');
      } else {
        console.debug('✅ Token still valid, no refresh needed');
      }

      const exp = this.keycloak.tokenParsed?.exp ?? 0;
      const millisecondsValid = exp * 1000 - Date.now();

      console.debug(
        `🕒 Token valid until: ${DateTime.fromSeconds(exp).toISO()}`,
      );
      console.debug(
        `⏱ Scheduling next refresh in ${((millisecondsValid * 0.8) / 1000).toFixed(0)}s`,
      );

      if (this.tokenRefresherHandle) {
        clearTimeout(this.tokenRefresherHandle);
      }

      const refreshInterval = Math.max(
        15 * 1000,
        Math.ceil(millisecondsValid * 0.8),
      );
      this.tokenRefresherHandle = setTimeout(() => {
        this.updateToken().catch(console.error);
      }, refreshInterval);
    } catch (error) {
      console.error('❌ Failed to refresh token:', error);
      runInAction(() => {
        void this.login();
      });
    }
  }

  private setupAccessTokenRefresh(keycloak: Keycloak) {
    keycloak.onAuthRefreshSuccess = async () => {
      const userInfo = await keycloak.loadUserProfile();
      runInAction(() => {
        this.user = userInfo;
        //this.realmRoleList = this.extractRoles(keycloak);
      });
    };
  }

  /* private extractRoles(keycloak: Keycloak): string[] | null {
    const roles = keycloak.tokenParsed?.realm_access?.roles || [];
    if (roles.length === 0) {
      return null;
    }
    return roles.filter((role): role is UserRole =>
      Object.values(UserRole).includes(role as UserRole),
    );
  } */

  public get token() {
    const token = this.keycloak?.token;

    if (!token) {
      // when using Keycloak in dev mode, the token might not be available
      if (this.disableKc) return '';

      this.failedInitialization = true;
      this.resetKeycloak();

      throw new Error('Keycloak token is not available');
    }

    return token;
  }

  private resetKeycloak() {
    this.keycloak = null;
    this.authenticated = false;
    this.initialized = false;
  }
}

const { provider, useStore } = setupMobX<KeycloakStore>();
export const KeycloakProvider = provider;
export const useKeycloakStore = useStore;
